// Shell aplikasi = composition root. Merakit topbar + sidebar + area konten,
// dan mendaftarkan modul fitur. Modul TIDAK mengimpor shell (satu arah).
import { currentUser, setSession } from './session';
import { esc } from '@domain/format';
import { LOGO_IFICS_PUTIH } from '@ui/logo';
import { logout, changeOwnPassword, changeOwnEmail } from '@modules/auth/auth.service';
import { kebijakan } from '@core/settings';
import { startIdleLogout } from '@core/idle';
import { siapkanSuara } from '@core/sound';
import { setTheme, THEMES, currentThemeId } from '@core/theme';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { renderIntake } from '@modules/intake/intake.view';
import { queueOf, openDetail } from '@modules/queue/queue.view';
import { renderSiap, renderSudah } from '@modules/queue/queue.special.view';
import { renderDashboard } from '@modules/dashboard/dashboard.view';
import { renderStatusHutangPiutang } from '@modules/payments/status-hub.view';
import { renderTutupKasir } from '@modules/payments/kasir.view';
import { renderLapServis } from '@modules/reports/reports.view';
import { renderPelanggan } from '@modules/pelanggan/pelanggan.view';
import { renderJasa } from '@modules/jasa/jasa.view';
import { renderSparepart } from '@modules/sparepart/sparepart.view';
import { renderPengaturan } from '@modules/settings/settings.view';
import { renderSurvei } from '@modules/survey/survey.view';
import { renderUndian } from '@modules/undian/undian.view';
import { renderKPI, renderTarget } from '@modules/kpi/kpi.view';
import { renderCRM } from '@modules/crm/crm.view';
import { renderAbsensi, renderRekapAbsensi } from '@modules/absensi/absensi.view';
import { renderBeban, renderKomisiHub } from '@modules/finance/finance.view';
import { renderKeuangan } from '@modules/finance/keuangan.view';
import { renderPenggajian } from '@modules/finance/gaji.view';
import { mountBell, refreshTopbarBell } from '@modules/notif/notif.view';
import { mountGlobalSearch } from '@modules/search/search.view';
import { enhanceTables } from '@ui/table';
import { setExportGuard } from '@ui/export-guard';
import { exportGuard } from '@modules/export/export.guard';
import { renderBackup } from '@modules/backup/backup.view';
import { renderPerluDiperhatikan, perluActionCount } from '@modules/perlu/perlu.view';
import { renderAuditApproval } from '@modules/audit/audit.view';
import { runReminders } from '@modules/perlu/reminders';
import { statusCounts } from '@modules/queue/queue.repo';
import { approvalActionCount } from '@modules/audit/audit.repo';
import { paymentActionCount } from '@modules/payments/payments.repo';
import { renderSupDashboard, renderSupReqMasuk, renderSupRiwayat, renderSupLaporan, renderSupDatabase } from '@modules/supplier-portal/portal.view';
import { reqMasukCount } from '@modules/supplier-portal/portal.repo';
import { onNavigate, onRefreshCounts, setMenuAktif } from './nav';
import { menuVis, fetchSetting } from './settings';
import { bisaBuka } from './perms';
import { FLOW } from '@domain/status';
import type { Role } from './types';

interface MenuItem {
  id: string;
  label: string;
  group: string;
  roles?: Role[]; // undefined = semua peran
  status?: string; // status tiket (untuk badge angka biru di sidebar)
  actionCount?: () => Promise<number>; // jumlah "perlu aksi" (badge 💬 attention) — dihitung per navigasi
  render: (root: HTMLElement) => void | Promise<void>;
}

const STATUS_ICON: Record<string, string> = {
  'Diterima': '📥', 'Diagnosa': '🔍', 'Menunggu Persetujuan': '⏳',
  'Dikerjakan': '🔧', 'Siap Diambil': '✅', 'Sudah Diambil': '📦',
};

const MENUS: MenuItem[] = [
  { id: 'dashboard', label: '🏠 Dashboard', group: 'Operasional', render: renderDashboard },
  // Penagihan & Pengingat DILEBUR ke sini sebagai sub-tab (Apo 21 Jul) — satu tempat untuk
  // "apa yang harus saya kerjakan". Badge 💬 menggantikan panel alert di Dashboard.
  { id: 'perlu_diperhatikan', label: '⚠️ Perlu Diperhatikan', group: 'Operasional', actionCount: perluActionCount, render: renderPerluDiperhatikan },
  { id: 'jasa', label: '🛠️ Jasa Servis', group: 'Operasional', roles: ['Owner', 'Manager', 'SPV', 'Admin Finance', 'CS'], render: renderJasa }, // dinaikkan (sering dipakai: pricelist+kalkulator CS); CRUD tetap O/M
  // Dinaikkan dari Pengaturan (Apo 23 Jul): katalog bisa 10.000 SKU dan sering dilihat teknisi/CS
  // untuk cek stok & harga — terlalu dalam kalau harus lewat Pengaturan. Kelola supplier tetap di sana.
  { id: 'sparepart', label: '🔩 Sparepart', group: 'Operasional', render: renderSparepart },
  { id: 'terima', label: '➕ Terima Servis', group: 'Operasional', roles: ['Owner', 'Manager', 'SPV', 'CS'], render: renderIntake },
  ...FLOW.map((s) => ({
    id: 'st_' + s.replace(/\s+/g, '_'),
    label: s === 'Siap Diambil' ? '✅ Bisa Diambil' : `${STATUS_ICON[s] ?? ''} ${s}`,
    group: 'Antrian Servis',
    status: s,
    render: s === 'Siap Diambil' ? renderSiap : (s === 'Sudah Diambil' ? renderSudah : queueOf(s)),
  })),
  { id: 'bayar', label: '💳 Status Hutang, Piutang', group: 'Keuangan', roles: ['Owner', 'Manager', 'Admin Finance', 'SPV', 'CS', 'Teknisi'], actionCount: paymentActionCount, render: renderStatusHutangPiutang }, // B1: gabung Status Pembayaran + Piutang + Hutang Supplier + DP (tab per-peran) · badge 💬 perlu-aksi per-peran
  { id: 'lap_servis', label: '📊 Laporan Servis', group: 'Laporan', roles: ['Owner', 'Manager', 'Admin Finance', 'SPV', 'Teknisi'], render: renderLapServis },
  { id: 'pelanggan', label: '👥 Pelanggan', group: 'Master Data', roles: ['Owner', 'Manager', 'SPV', 'CS'], render: renderPelanggan },
  // 👔 Karyawan & 🏢 Cabang DIHAPUS dari menu utama (Apo 21 Jul) — keduanya master data yang
  // jarang disentuh, tempatnya di ⚙️ Pengaturan & Profil. Izinnya ikut pindah ke `pengaturan.*`.
  { id: 'crm', label: '🤝 CRM / Loyalty', group: 'Master Data', roles: ['Owner', 'Manager', 'SPV', 'CS'], render: renderCRM },
  // Sparepart & Supplier: dihapus dari menu utama (keputusan Apo) — hanya via Pengaturan → tab Sparepart/Supplier (O/M).
  // B2 Fase 1 — Portal Supplier (role Supplier saja): read-only. Import/Sync menyusul.
  { id: 'sup_dash', label: '🏭 Dashboard', group: 'Supplier', roles: ['Supplier'], render: renderSupDashboard },
  { id: 'sup_req', label: '📥 Request Masuk', group: 'Supplier', roles: ['Supplier'], actionCount: reqMasukCount, render: renderSupReqMasuk },
  { id: 'sup_riwayat', label: '📦 Riwayat Serah/Retur', group: 'Supplier', roles: ['Supplier'], render: renderSupRiwayat },
  { id: 'sup_lap', label: '📊 Laporan & Piutang', group: 'Supplier', roles: ['Supplier'], render: renderSupLaporan },
  { id: 'sup_db', label: '🗄️ Database Sparepart', group: 'Supplier', roles: ['Supplier'], render: renderSupDatabase },
  // B1: Hutang Supplier / Laporan DP / Laporan Piutang → dipindah jadi tab di menu "Status Hutang, Piutang".
  { id: 'lap_komisi', label: '🧰 Teknisi / Komisi', group: 'Laporan', roles: ['Owner', 'Manager', 'Admin Finance', 'SPV'], render: renderKomisiHub },
  // Tutup Kasir di menu UTAMA (bukan tab): dipakai CS tiap sore — jangan sembunyikan di balik tab menu keuangan.
  { id: 'tutup_kasir', label: '📅 Tutup Kasir', group: 'Keuangan', roles: ['Owner', 'Manager', 'Admin Finance', 'SPV', 'CS'], render: renderTutupKasir },
  // 💰 Keuangan terpadu (Apo 21 Jul): Laba-Rugi + Arus Kas + Posisi Keuangan + Beban jadi
  // satu menu bertab. Menu Beban tetap berdiri sendiri karena dipakai harian oleh Finance.
  { id: 'keuangan', label: '💰 Keuangan', group: 'Keuangan', roles: ['Owner', 'Manager', 'Admin Finance'], render: renderKeuangan },
  { id: 'beban', label: '💸 Beban / Pengeluaran', group: 'Keuangan', roles: ['Owner', 'Manager', 'Admin Finance'], render: renderBeban },
  // Penggajian TANPA SPV: RLS `read_emp_self` membuat SPV bisa membaca seluruh baris karyawan
  // di cabangnya, jadi menu gaji sengaja dijauhkan dari peran itu.
  { id: 'penggajian', label: '💰 Penggajian', group: 'Keuangan', roles: ['Owner', 'Manager', 'Admin Finance'], render: renderPenggajian },

  { id: 'survei', label: '⭐ Survei Kepuasan', group: 'Laporan', roles: ['Owner', 'Manager', 'SPV', 'CS', 'Admin Finance', 'Teknisi'], render: renderSurvei },
  { id: 'undian', label: '🎁 Undian Survei', group: 'Laporan', roles: ['Owner', 'Manager', 'Admin Finance'], render: renderUndian },
  { id: 'kpi', label: '📈 KPI Kinerja', group: 'Laporan', roles: ['Owner', 'Manager', 'SPV'], render: renderKPI },
  { id: 'target', label: '🎯 Target / KRA', group: 'Pengaturan', roles: ['Owner', 'Manager'], render: renderTarget },
  { id: 'absensi', label: '🕒 Absensi', group: 'Kehadiran', render: renderAbsensi },
  { id: 'rekap_absensi', label: '📋 Rekap Absensi', group: 'Kehadiran', roles: ['Owner', 'Manager', 'SPV'], render: renderRekapAbsensi },
  // SPV ikut boleh membuka Pengaturan, TAPI bawaannya hanya tab 👔 Karyawan (kelola timnya) —
  // sisanya tertutup lewat BAWAAN_AKSI. Tanpa ini, menghapus menu Karyawan dari sidebar akan
  // mencabut kemampuan SPV mengelola timnya sama sekali.
  { id: 'pengaturan', label: '⚙️ Pengaturan & Profil', group: 'Pengaturan', roles: ['Owner', 'Manager', 'SPV'], render: renderPengaturan },
  { id: 'audit', label: '🛡️ Audit & Approval', group: 'Pengaturan', roles: ['Owner', 'Manager', 'SPV'], actionCount: approvalActionCount, render: renderAuditApproval }, // SPV: hanya tab Perlu Approval (koreksi absensi + cuti timnya)
  { id: 'backup', label: '💾 Backup Data', group: 'Pengaturan', roles: ['Owner', 'Manager'], render: renderBackup },
  // Arsip Transaksi: dipindah jadi tab di dalam Pengaturan (settings TABS 'arsip'). Menu standalone dihapus (permintaan Apo).
];

const allowed = (m: MenuItem): boolean => {
  const u = currentUser();
  if (!u) return false;
  // Supplier (portal) HANYA lihat menu portal (roles memuat 'Supplier'); menu umum tanpa roles tak bocor ke supplier.
  if (u.role === 'Supplier') return !!m.roles?.includes('Supplier');
  if (m.roles && !m.roles.includes(u.role)) return false;
  // Hak Akses (grup + override karyawan): "tidak bisa akses" → menunya hilang dari sidebar.
  // Owner tak pernah dibatasi. `menuVis` lama tetap dihormati untuk setelan yang sudah tersimpan.
  if (u.role === 'Owner') return true;
  if (menuVis()[u.role]?.[m.id] === false) return false;
  return bisaBuka(m.id);
};

/** Menu yang boleh dibuka pengguna saat ini — dipakai pencarian global agar tak pernah
 *  menawarkan menu yang haknya dicabut. */
export const menuBoleh = (): { id: string; label: string; group: string }[] =>
  MENUS.filter(allowed).map((m) => ({ id: m.id, label: m.label, group: m.group }));

/** Metadata menu (id/label/group/roles) utk Hak Akses — dibaca RUNTIME di settings.view (hindari cycle init). */
export const MENU_META = (): { id: string; label: string; group: string; roles?: Role[] }[] =>
  MENUS.map((m) => ({ id: m.id, label: m.label, group: m.group, roles: m.roles }));

let activeId = 'dashboard';
let homeTab = 'dashboard'; // tab utama tak bisa ditutup (Dashboard, atau menu portal pertama utk Supplier)
let sideCounts: Record<string, number> = {};   // jumlah tiket per status (badge biru sidebar)
let actionCounts: Record<string, number> = {}; // jumlah "perlu aksi" per menu (badge 💬 attention)
const collapsed: Record<string, boolean> = {};  // grup sidebar yg dilipat (default terbuka)

/** Rakit ulang isi sidebar: grup (lipat/buka) + item + badge angka status. Port buildSidebar v0.18. */
function buildSidebar(): void {
  const side = document.querySelector<HTMLElement>('#side');
  if (!side) return;
  const visible = MENUS.filter(allowed);
  const groups = [...new Set(visible.map((m) => m.group))];
  // Nama & peran di kepala laci. Di batang atas keduanya tak muat sebaris dengan tombol pada
  // layar HP, dan breadcrumb layar TIDAK memuat nama (hanya "Perlu Diperhatikan" yang memuat;
  // portal supplier dan Absensi tak punya breadcrumb sama sekali) — padahal absensi justru
  // paling butuh kepastian sedang masuk sebagai akun siapa.
  const me = currentUser();
  const kepala = me ? `<div class="side-me">${esc(me.nama)} · ${esc(me.role)}</div>` : '';
  side.innerHTML = kepala + groups.map((g) => {
    const col = !!collapsed[g];
    const items = visible.filter((m) => m.group === g).map((m) => {
      const nStat = m.status ? (sideCounts[m.status] ?? 0) : 0;
      const nAct = actionCounts[m.id] ?? 0;
      const pills: string[] = [];
      if (nStat > 0) pills.push(`<span style="background:var(--blue);color:#fff;border-radius:10px;padding:0 7px;font-size:11px;font-weight:700">${nStat}</span>`);
      if (nAct > 0) pills.push(`<span title="Perlu ditindak" style="background:var(--danger);color:#fff;border-radius:10px;padding:0 7px;font-size:11px;font-weight:700;white-space:nowrap">💬 ${nAct}</span>`);
      const badge = pills.length ? `<span style="margin-left:auto;display:flex;gap:4px;align-items:center">${pills.join('')}</span>` : '';
      return `<a data-id="${m.id}" class="${m.id === activeId ? 'active' : ''}" style="display:flex;align-items:center">${m.label}${badge}</a>`;
    }).join('');
    return `<div class="grp" data-grp="${esc(g)}" role="button" tabindex="0" style="cursor:pointer;user-select:none;display:flex;align-items:center;gap:6px"><span style="font-size:10px;width:10px;display:inline-block">${col ? '▸' : '▾'}</span><span>${esc(g)}</span></div><div${col ? ' style="display:none"' : ''}>${items}</div>`;
  }).join('');
  side.querySelectorAll<HTMLElement>('a[data-id]').forEach((a) =>
    a.addEventListener('click', () => openTab(a.dataset.id ?? 'dashboard')));
  side.querySelectorAll<HTMLElement>('.grp[data-grp]').forEach((g) =>
    g.addEventListener('click', () => { const nm = g.dataset.grp ?? ''; collapsed[nm] = !collapsed[nm]; buildSidebar(); }));
}

/** Ambil jumlah tiket per status → perbarui badge sidebar (best-effort, non-blocking). */
async function loadSideCounts(): Promise<void> {
  // Segarkan hak akses tiap pindah menu. Tanpa ini, hak yang dicabut sekarang baru terasa
  // setelah orangnya logout — bisa berarti satu shift penuh masih bisa mengubah data.
  try { await Promise.all([fetchSetting('akses', {}), fetchSetting('akses_user', {})]); } catch { /* pakai yang sudah ada */ }
  try { sideCounts = await statusCounts(); } catch { /* badge opsional */ }
  // Badge 💬 "perlu aksi" — hitung utk menu yg punya resolver & terlihat peran ini (best-effort, paralel).
  try {
    const acts = MENUS.filter((m) => m.actionCount && allowed(m));
    const res = await Promise.all(acts.map((m) => m.actionCount!().catch(() => 0)));
    actionCounts = Object.fromEntries(acts.map((m, i) => [m.id, res[i] ?? 0]));
  } catch { /* badge opsional */ }
  buildSidebar();
}

// ===== Multi-tab (newtab) — port openTabs/buildTabs/switchTab/closeTab v0.18 =====
let openTabs: string[] = ['dashboard'];

function buildTabs(): void {
  const tb = document.querySelector<HTMLElement>('#tabBar');
  if (!tb) return;
  tb.innerHTML = openTabs.map((id) => {
    const m = MENUS.find((x) => x.id === id);
    const label = m ? m.label : id;
    return `<div class="wtab ${id === activeId ? 'active' : ''}" data-tab="${esc(id)}"><span>${esc(label)}</span>${id === homeTab ? '' : `<button class="tx" data-close="${esc(id)}" title="Tutup tab">×</button>`}</div>`;
  }).join('');
  tb.querySelectorAll<HTMLElement>('.wtab[data-tab]').forEach((el) =>
    el.addEventListener('click', () => switchTab(el.dataset.tab ?? 'dashboard')));
  tb.querySelectorAll<HTMLElement>('.tx[data-close]').forEach((b) =>
    b.addEventListener('click', (e) => { e.stopPropagation(); closeTab(b.dataset.close ?? ''); }));
}

function renderActive(): void {
  setMenuAktif(activeId);
  const m = MENUS.find((x) => x.id === activeId && allowed(x));
  if (!m) return;
  const content = document.querySelector<HTMLElement>('#content');
  if (content) void m.render(content);
  // Kembali ke atas tiap ganti menu. Tanpa ini, membuka menu baru saat menu lama digulir ke
  // bawah membuat layar mendarat di tengah halaman — keluhan Apo pada menu Approval.
  window.scrollTo({ top: 0 });
  content?.scrollTo({ top: 0, left: 0 });
}

// Tombol ← di topbar DIHAPUS (permintaan Apo) — navigasi lewat tab & sidebar; tiap popup punya ← sendiri.
/** Buka menu di tab (tambah tab bila belum ada) + jadikan aktif. */
function openTab(id: string): void {
  const m = MENUS.find((x) => x.id === id && allowed(x));
  if (!m) return;
  if (!openTabs.includes(id)) openTabs.push(id);
  activeId = id;
  buildSidebar(); buildTabs(); renderActive();
  void loadSideCounts();
}

function switchTab(id: string): void {
  activeId = id;
  buildSidebar(); buildTabs(); renderActive();
  void loadSideCounts();
}

function closeTab(id: string): void {
  const i = openTabs.indexOf(id);
  if (i < 0 || id === homeTab) return; // tab utama tak bisa ditutup
  openTabs.splice(i, 1);
  if (!openTabs.length) openTabs = [homeTab];
  if (activeId === id) activeId = openTabs[Math.max(0, i - 1)] ?? openTabs[0]!;
  buildSidebar(); buildTabs(); renderActive();
}

export function renderShell(): void {
  const app = document.querySelector<HTMLElement>('#app')!;
  const u = currentUser()!;
  const home = MENUS.find(allowed)?.id ?? 'dashboard'; // Supplier mendarat di menu portal pertama, bukan Dashboard
  homeTab = home; openTabs = [home]; activeId = home;

  app.innerHTML = `
    <div class="topbar">
      <button class="pill-btn burger" id="burgerBtn" aria-label="Buka menu" aria-expanded="false">☰</button>
      <img class="brand-logo" src="${LOGO_IFICS_PUTIH}" alt="iFiCs">
      <span class="grow"></span>
      <span class="who">${esc(u.nama)} · ${esc(u.role)}</span>
      <span id="searchHost"></span>
      <button class="pill-btn" id="themeBtn" title="Pilih tema tampilan" aria-label="Pilih tema tampilan">🎨</button>
      <button class="pill-btn" id="refreshBtn" title="Muat ulang data menu ini">🔄</button>
      <button class="pill-btn" id="absBtn" title="Absen masuk / pulang" aria-label="Absen masuk atau pulang" style="position:relative">🕒<span id="absDot" style="display:none;position:absolute;top:-3px;right:-3px;width:9px;height:9px;border-radius:50%;background:var(--danger);border:2px solid var(--navy)"></span></button>
      <span id="bellHost"></span>
      <button class="pill-btn" id="pwBtn" title="Ganti password saya">🔑</button>
      <button class="pill-btn" id="logoutBtn">Keluar</button>
    </div>
    <div class="layout">
      <nav class="side" id="side"></nav>
      <section class="maincol">
        <div class="tabbar" id="tabBar"></div>
        <main class="content" id="content"></main>
      </section>
    </div>`;

  siapkanSuara();   // buka jalur suara pada sentuhan pertama (kebijakan autoplay peramban)
  buildSidebar();
  // Bar tab & laci menu menempel tepat di bawah batang atas. Tingginya berubah-ubah — isi
  // batang menyusut di layar sempit, dan bertambah setinggi status bar saat aplikasi dipasang
  // di layar depan HP. Diukur langsung, bukan ditebak, supaya tak ada celah maupun tumpang tindih.
  const topbarEl = app.querySelector<HTMLElement>('.topbar')!;
  const ukurTopbar = (): void => document.documentElement.style.setProperty('--topbar-h', `${topbarEl.offsetHeight}px`);
  ukurTopbar();
  // `box: 'border-box'` WAJIB: yang berubah saat aplikasi dipasang di layar depan HP adalah
  // padding-nya (setinggi status bar), bukan isinya — pengamat bawaan hanya melihat kotak isi
  // sehingga tak pernah menyala, dan laci menu tetap memakai tinggi lama lalu menyelinap
  // ke belakang status bar.
  new ResizeObserver(ukurTopbar).observe(topbarEl, { box: 'border-box' });

  // Di layar sempit sidebar disembunyikan di luar layar; tanpa tombol ini menunya tak bisa
  // dijangkau sama sekali dari HP.
  const side = app.querySelector<HTMLElement>('#side')!;
  const burger = app.querySelector<HTMLButtonElement>('#burgerBtn')!;
  const tutupSide = (): void => { side.classList.remove('open'); burger.setAttribute('aria-expanded', 'false'); };
  burger.addEventListener('click', () => {
    const buka = side.classList.toggle('open');
    burger.setAttribute('aria-expanded', String(buka));
  });
  side.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('a[data-id]')) tutupSide(); });
  const doLogout = async (idle = false): Promise<void> => {
    try { if (idle) localStorage.setItem('ifics-idle', '1'); } catch { /* mode privat */ }
    await logout();
    setSession(null);
    location.reload();
  };
  app.querySelector('#logoutBtn')!.addEventListener('click', () => void doLogout(false));
  // Auto-logout idle (settable di Pengaturan → Kebijakan). Dipasang sekali; reload membersihkannya.
  startIdleLogout(kebijakan().autoLogoutMenit, () => void doLogout(true));
  app.querySelector('#pwBtn')!.addEventListener('click', () => gantiPasswordDialog());
  // 🎨 Pemilih tema — buka dialog daftar tema (mudah ditambah lewat registry core/theme.ts).
  app.querySelector('#themeBtn')?.addEventListener('click', () => themePickerDialog());
  // Tombol 🔄 global: render ulang menu/tab aktif + refresh badge (menutup semua menu, satu titik).
  app.querySelector('#refreshBtn')!.addEventListener('click', () => { renderActive(); void loadSideCounts(); });

  // Shortcut absen di topbar (Apo 28 Jul): karyawan absen tiap hari, jangan sampai harus menelusuri
  // sidebar dulu. Titik merah = belum ada catatan masuk hari ini — pengingat tanpa perlu membuka menu.
  const absBtn = app.querySelector<HTMLButtonElement>('#absBtn');
  if (absBtn) {
    // Supplier tak punya jam kerja di toko → tombolnya tak berlaku untuk mereka.
    if (currentUser()?.role === 'Supplier') absBtn.remove();
    else {
      absBtn.addEventListener('click', () => openTab('absensi'));
      void tandaiBelumAbsen(app);
    }
  }
  // Pintasan PWA (tekan-lama ikon di layar depan HP → "Absen Masuk/Pulang") membuka /?menu=absensi.
  // Menu yang diminta hanya dibuka bila peran ini memang berhak — jangan percaya isi URL.
  try {
    const minta = new URLSearchParams(window.location.search).get('menu');
    if (minta && menuBoleh().some((m2) => m2.id === minta)) {
      openTab(minta);
      history.replaceState(null, '', window.location.pathname);
    }
  } catch { /* URL aneh → abaikan, buka dashboard seperti biasa */ }

  const bellHost = app.querySelector<HTMLElement>('#bellHost');
  if (bellHost) void mountBell(bellHost);
  // Pencarian global servisan (ketik/scan QR → loncat ke posisi tiket). Supplier tak punya akses tiket → tak dipasang.
  const searchHost = app.querySelector<HTMLElement>('#searchHost');
  if (searchHost && u.role !== 'Supplier') mountGlobalSearch(searchHost);

  // Global: setiap tabel .data yang muncul di konten → header klik-sort + lebar kolom drag (debounce).
  const contentEl = app.querySelector<HTMLElement>('#content');
  if (contentEl) {
    let et = 0;
    new MutationObserver(() => { window.clearTimeout(et); et = window.setTimeout(() => enhanceTables(contentEl), 60); })
      .observe(contentEl, { childList: true, subtree: true });
  }

  onNavigate(navigate);            // modul lain berpindah menu tanpa impor shell
  onRefreshCounts(() => { void loadSideCounts(); refreshTopbarBell(); }); // badge sidebar + lonceng auto-update pasca transisi/verifikasi
  setExportGuard(exportGuard);     // kontrol export terpusat (O/M bebas · SPV approval · sisanya blok)
  // Nomor tiket/nota/servis (kelas .tno-link, dibuat enhanceTables) → klik buka detail tiket, di SEMUA menu.
  // Detail = fungsional penuh (transisi/refund/arsip sesuai izin). refresh = renderActive → menu aktif
  // (antrian/laporan) ikut ter-render ulang setelah aksi, agar tak basi (sama seperti tombol 🔍 di baris).
  if (!(window as unknown as { __tnoClick?: boolean }).__tnoClick) {
    (window as unknown as { __tnoClick?: boolean }).__tnoClick = true;
    document.addEventListener('click', (e) => {
      const a = (e.target as HTMLElement).closest('.tno-link') as HTMLElement | null;
      if (!a?.dataset['no']) return;
      e.preventDefault();
      void openDetail(a.dataset['no'], () => renderActive());
    });
  }
  void runReminders();             // reminder harian per-peran (client-run saat login)
  void loadSideCounts();           // badge angka status di sidebar

  buildTabs();
  renderActive();
}

/** Entry publik (dipakai goto dari modul): buka menu `id` sebagai tab aktif. */
export function navigate(id: string): void { openTab(id); }


/** Ganti password sendiri — tersedia untuk SEMUA peran (termasuk Supplier), dari topbar.
 *  Wajib untuk deploy: password sementara dari admin harus bisa diganti pemiliknya. */
/** Pemilih tema — kartu swatch per tema; klik = terapkan langsung (live) + simpan. Tambah tema
 *  cukup di registry `core/theme.ts` → otomatis muncul di sini. */
function themePickerDialog(): void {
  const bg = openModal(`<div class="modal-head"><h3>🎨 Pilih Tema</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Klik tema untuk menerapkannya langsung. Pilihan tersimpan di perangkat ini.</div>
      <div id="thm_grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px"></div></div>
    <div class="modal-foot"><button class="btn primary" id="thm_ok">Selesai</button></div>`);
  const grid = bg.querySelector('#thm_grid') as HTMLElement;
  const gambar = (): void => {
    const aktif = currentThemeId();
    grid.innerHTML = THEMES.map((t) => `<button class="thm-card" data-id="${esc(t.id)}" aria-pressed="${t.id === aktif}"
      style="text-align:left;border:2px solid ${t.id === aktif ? 'var(--blue)' : 'var(--line)'};background:var(--surface);border-radius:10px;padding:9px;cursor:pointer;font:inherit;color:var(--txt)">
      <span style="display:flex;gap:4px;margin-bottom:7px">
        <i style="width:26px;height:26px;border-radius:6px;background:${esc(t.swatch[0])};border:1px solid var(--line)"></i>
        <i style="width:26px;height:26px;border-radius:6px;background:${esc(t.swatch[1])};border:1px solid var(--line)"></i>
        <i style="width:26px;height:26px;border-radius:6px;background:${esc(t.swatch[2])}"></i></span>
      <span style="font-size:13px;font-weight:600">${esc(t.nama)}</span>
      ${t.id === aktif ? '<span style="float:right;color:var(--blue)">✓</span>' : ''}</button>`).join('');
    grid.querySelectorAll<HTMLElement>('.thm-card').forEach((c) => c.addEventListener('click', () => { setTheme(c.dataset['id'] ?? 'terang'); gambar(); }));
  };
  gambar();
  bg.querySelector('#thm_ok')?.addEventListener('click', () => bg.remove());
}


/** Titik merah pada tombol absen bila hari ini belum tercatat masuk. Best-effort — kegagalan
 *  jaringan tak boleh menghalangi apa pun, cukup titiknya tak muncul. */
async function tandaiBelumAbsen(app: HTMLElement): Promise<void> {
  const u = currentUser(); if (!u) return;
  try {
    const { todayAttendance } = await import('@modules/absensi/absensi.repo');
    const { todayISO } = await import('@domain/format');
    const rec = await todayAttendance(u.id, todayISO());
    const dot = app.querySelector<HTMLElement>('#absDot');
    if (dot && !rec?.masuk_at) dot.style.display = '';
  } catch { /* diam — tombolnya tetap bisa dipakai */ }
}



/**
 * Ganti email login (Owner). Email login = kunci jalur "lupa password", jadi perubahannya dijaga
 * password + konfirmasi silang: Supabase mengirim tautan ke alamat LAMA dan BARU, dan email baru
 * baru berlaku setelah keduanya diklik. Alamat lama yang tak bisa dibuka = perubahan tak jadi.
 */
function pasangPanelEmail(bg: HTMLElement): void {
  const slot = bg.querySelector<HTMLElement>('#email_slot'); if (!slot) return;
  slot.innerHTML = `<div style="border-top:1px solid var(--line);margin-top:14px;padding-top:12px">
      <div style="font-weight:600;margin-bottom:4px">📧 Email Login</div>
      <div class="scope-note" style="font-size:12px">Dipakai untuk masuk dan untuk memulihkan akun saat lupa password. Setelah diubah, <b>tautan konfirmasi dikirim ke email lama DAN email baru</b> — keduanya harus diklik.</div>
      <div class="field" style="margin-top:8px"><label>Email baru</label><input id="em_baru" type="email" placeholder="nama@email.com"></div>
      <div class="field"><label>Password Anda <span class="req">*</span></label><input id="em_pass" type="password" autocomplete="current-password"></div>
      <button class="btn" id="em_ok">Kirim Konfirmasi</button>
      <div class="err" id="em_e"></div></div>`;
  const err = slot.querySelector<HTMLElement>('#em_e')!;
  const btn = slot.querySelector<HTMLButtonElement>('#em_ok')!;
  btn.addEventListener('click', () => void (async () => {
    err.textContent = ''; btn.disabled = true; btn.textContent = 'Mengirim…';
    try {
      await changeOwnEmail((slot.querySelector<HTMLInputElement>('#em_baru'))!.value, (slot.querySelector<HTMLInputElement>('#em_pass'))!.value);
      toast('📧 Konfirmasi dikirim ke email lama & baru — klik keduanya untuk menyelesaikan.');
      (slot.querySelector<HTMLInputElement>('#em_pass'))!.value = '';
    } catch (e) { err.textContent = (e as Error).message; }
    btn.disabled = false; btn.textContent = 'Kirim Konfirmasi';
  })());
}

/**
 * Panel 2FA di dialog Keamanan Akun — hanya Owner (akun yang memegang uang & tombol hapus).
 * Dipasang belakangan (async) supaya dialog password tetap terbuka seketika walau jaringan lambat.
 */
async function pasangPanel2FA(bg: HTMLElement): Promise<void> {
  const slot = bg.querySelector<HTMLElement>('#mfa_slot'); if (!slot) return;
  const mfa = await import('@modules/auth/mfa.service');
  const gambar = async (): Promise<void> => {
    const aktif = await mfa.activeFactorId();
    slot.innerHTML = `<div style="border-top:1px solid var(--line);margin-top:14px;padding-top:12px">
      <div style="font-weight:600;margin-bottom:4px">🔐 Verifikasi Dua Langkah (2FA)</div>
      <div class="scope-note" style="font-size:12px">${aktif
        ? 'Aktif — setiap login meminta 6 angka dari aplikasi authenticator Anda. Password bocor saja tidak cukup untuk masuk.'
        : 'Belum aktif. Bila dinyalakan, login butuh password <b>dan</b> kode dari aplikasi authenticator (mis. Google Authenticator).'}</div>
      <div id="mfa_area" style="margin-top:8px"></div>
      <button class="btn" id="mfa_btn" style="${aktif ? 'border-color:var(--danger);color:var(--danger)' : ''}">${aktif ? 'Matikan 2FA' : 'Aktifkan 2FA'}</button>
      <div class="err" id="mfa_e"></div></div>`;
    const err = slot.querySelector<HTMLElement>('#mfa_e')!;
    slot.querySelector('#mfa_btn')?.addEventListener('click', () => void (async () => {
      err.textContent = '';
      try {
        if (aktif) {
          if (!confirm('Matikan 2FA? Login kembali hanya butuh password.')) return;
          await mfa.disableTotp(aktif); toast('🔓 2FA dimatikan'); await gambar(); return;
        }
        const d = await mfa.enrollTotp();
        const area = slot.querySelector<HTMLElement>('#mfa_area')!;
        area.innerHTML = `<div style="background:#fff;padding:8px;border-radius:8px;display:inline-block">${d.qrSvg.startsWith('data:') ? `<img src="${esc(d.qrSvg)}" alt="QR 2FA" style="width:170px;height:170px">` : d.qrSvg}</div>
          <div style="font-size:12px;color:var(--muted);margin:6px 0">Pindai QR di aplikasi authenticator. Tak bisa memindai? Ketik kode ini: <b style="font-family:ui-monospace,monospace">${esc(d.secret)}</b></div>
          <input id="mfa_code" inputmode="numeric" maxlength="6" placeholder="6 angka dari aplikasi" style="width:100%;padding:9px;border:1px solid var(--line);border-radius:8px;letter-spacing:.25em;text-align:center">
          <button class="btn primary" id="mfa_verif" style="margin-top:8px">Verifikasi & Aktifkan</button>`;
        (slot.querySelector<HTMLButtonElement>('#mfa_btn'))!.style.display = 'none';
        area.querySelector('#mfa_verif')?.addEventListener('click', () => void (async () => {
          err.textContent = '';
          try {
            await mfa.verifyEnroll(d.factorId, (area.querySelector<HTMLInputElement>('#mfa_code'))!.value);
            toast('🔐 2FA aktif — login berikutnya minta kode'); await gambar();
          } catch (e2) { err.textContent = (e2 as Error).message; }
        })());
      } catch (e) { err.textContent = (e as Error).message; }
    })());
  };
  await gambar();
}

function gantiPasswordDialog(): void {
  const u = currentUser();
  const bg = openModal(`<div class="modal-head"><h3>🔑 Keamanan Akun</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Untuk akun <b>${esc(u?.nama ?? '')}</b>. Minimal 6 karakter. Pakai kombinasi yang tak mudah ditebak.</div>
      <div class="field"><label>Password lama <span class="req">*</span></label><input id="pw_old" type="password" autocomplete="current-password"></div>
      <div class="field"><label>Password baru</label><input id="pw_new" type="password" autocomplete="new-password"></div>
      <div class="field"><label>Ulangi password baru</label><input id="pw_new2" type="password" autocomplete="new-password"></div>
      <div class="err" id="pw_err"></div>
      <div id="email_slot"></div>
      <div id="mfa_slot"></div></div>
    <div class="modal-foot"><button class="btn" id="pw_cancel">Tutup</button><button class="btn primary" id="pw_save">Simpan Password</button></div>`);
  if (u?.role === 'Owner') { pasangPanelEmail(bg); void pasangPanel2FA(bg); }   // khusus Owner (keputusan Apo)
  const err = (m: string): void => { const e = bg.querySelector('#pw_err') as HTMLElement; if (e) e.textContent = m; };
  bg.querySelector('#pw_cancel')?.addEventListener('click', () => bg.remove());
  const save = bg.querySelector('#pw_save') as HTMLButtonElement;
  save.addEventListener('click', async () => {
    const lama = (bg.querySelector('#pw_old') as HTMLInputElement).value;
    const p1 = (bg.querySelector('#pw_new') as HTMLInputElement).value;
    const p2 = (bg.querySelector('#pw_new2') as HTMLInputElement).value;
    if (!lama.trim()) { err('Password lama wajib diisi.'); return; }
    if (p1 !== p2) { err('Kedua password tidak sama.'); return; }
    err(''); save.disabled = true;
    try {
      await changeOwnPassword(lama, p1);
      bg.remove();
      toast('✅ Password berhasil diganti. Pakai yang baru saat login berikutnya.');
    } catch (e) { err((e as Error).message); save.disabled = false; }
  });
}